import debounce from 'lodash/debounce';
import pick from 'lodash/pick';
import {AttrsSchema, Component, jsx} from 'panel';

import {Section, SectionItem} from 'common/components/items-menu/types';
import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {reporter} from 'common/report/error-reporting';
import {currentProjectStore} from 'common/report/stores/project-details';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {getUserIdentityFromCookie} from 'common/report/util/user-identity-from-cookie';
import {
  addRecentItem,
  DEFAULT_SEARCH_QUERY_STRING,
  executeSearch,
  getDisplayContext,
  getRecentItems,
  getSearchContextsWithMaybeExplore,
  getSearchIconFromDisplayContext,
  PROMPT_ALLOW_STORAGE_STRING,
  removeRecentItem,
} from 'common/report/util/search';
import {
  ContentDiscoveryType,
  getSearchCommonTrackingProperties,
  searchEventPrefix,
  SearchSource,
  SearchTrackedEvent,
  SearchTrackedProperty,
} from 'common/report/util/search/tracking';
import {
  RecentSearchItem,
  RecentSearchItemType,
  SearchDisplayContext,
  SearchDisplayContextDisplayName,
  SearchQueryFilters,
  SearchResult,
} from 'common/report/util/search/types';
import activeColorScheme from 'common/util/colors';
import {formatNumber} from 'common/util';
import {useStore} from 'common/util/component-helpers';
import {formatDate} from 'common/util/date';
import {emitTrackEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {findKeyByValue} from 'common/util/typescript';
import {truncate} from 'common/util/string';
import {SelectDropdown} from 'common/components/select';
import {addOrUpdateWorkspaceIdFromUrl} from 'common/report/util/url';

import {globalSearchBrowseReports} from '../constants';
import {template} from './template';

interface GlobalSearchJsxProps {
  attrs?: {
    open?: boolean;
  };
}

interface GlobalSearchState {
  allowRecentItems?: boolean;
  focusedItemKey: string;
  errored?: boolean;
  loading?: boolean;
  recentSearchItems: RecentSearchItem[];
  searchedText?: string;
  searchResults: SearchResult[];
}

function itemCountRenderer(item: SectionItem) {
  return (
    <div style={{'align-items': `center`, display: `flex`}}>
      <span sel='.value' style={{'margin-right': `8px`}}>
        {item.report === SearchDisplayContext.Explore ? `-` : formatNumber(item.count || 0)}
      </span>
      {item.isReadOnly ? (
        {}
      ) : (
        <div style={{display: `flex`}}>
          <SvgIconEl attrs={{icon: `person`}} style={{'margin-right': `16px`}} />
          <TooltipEl attrs={{placement: Placement.Top}}>Unique views (30 days)</TooltipEl>
        </div>
      )}
    </div>
  );
}

function leftIconRenderer({icon, tooltipText, isErrored}: {icon: string; tooltipText?: string; isErrored?: boolean}) {
  let color = null;
  if (isErrored) {
    color = activeColorScheme.red1;
  }

  const iconComponent = <SvgIconEl attrs={{icon, color}} style={{margin: `8px`}} />;
  if (tooltipText) {
    return (
      <div style={{display: `flex`}}>
        {iconComponent}
        <TooltipEl attrs={{placement: Placement.Top}}>{tooltipText}</TooltipEl>
      </div>
    );
  }
  return iconComponent;
}

export class GlobalSearch extends Component<GlobalSearchState, GlobalSearchJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<GlobalSearchJsxProps['attrs']> {
    return {
      open: `boolean`,
    };
  }

  get config() {
    return {
      template,
      defaultState: {
        allowRecentItems: true,
        focusedItemKey: null,
        errored: false,
        loading: false,
        recentSearchItems: [],
        searchResults: [],
        searchedText: DEFAULT_SEARCH_QUERY_STRING,
      },
      helpers: {
        enableBrowseAll: () => {
          const {errored, searchResults, loading} = this.state;
          return !errored && !loading && searchResults.length === 0;
        },
        searchSectionsFromInput: (input: string): Section[] => {
          const {errored, loading, searchedText, searchResults} = this.state;
          if (input) {
            const items = searchResults.map(
              (item: SearchResult): RecentSearchItem => ({
                count: item.unique_count,
                description: item.description,
                href: item.link,
                icon: getSearchIconFromDisplayContext(getDisplayContext(item)),
                isOwner: item.user_id === getUserIdentityFromCookie()?.id,
                key: `${RecentSearchItemType.Entity}-${item.id}`,
                label: truncate(item.name, 50),
                report: getDisplayContext(item),
                subLabel: `${item.user || `Unknown User`} · ${formatDate(new Date(item.last_edited))}`,
                type: RecentSearchItemType.Entity,
                user: item.user,
                value: item.name,
              }),
            );
            const results = {
              isLoading: loading,
              hideTopDivider: true,
              actionsRenderer: (item: SectionItem) => (!errored && items.length > 0 ? itemCountRenderer(item) : null),
              iconRenderer: (item: SectionItem) => {
                return leftIconRenderer({
                  icon: item.icon,
                  tooltipText: SearchDisplayContextDisplayName[findKeyByValue(SearchDisplayContext, item.report)],
                  isErrored: errored,
                });
              },
              items:
                items.length > 0
                  ? items.slice(0, 5)
                  : [
                      {
                        icon: errored ? `warning` : `search`,
                        isReadOnly: true,
                        label: errored ? `Something went wrong` : `No items match your search`,
                        subLabel: errored ? `Try another search, or contact support if this error persists.` : null,
                      },
                    ],
            };
            return [this.getBrowseItem(searchedText), results];
          }
          return null;
        },
        getSearchSections: (): Section[] => {
          const {allowRecentItems, searchedText} = this.state;
          const recentSearchEntities = this.getRecentSearchEntities();
          let items = [];
          if (!searchedText) {
            items =
              recentSearchEntities?.length > 0
                ? recentSearchEntities.slice(0, 5).map(
                    (item): RecentSearchItem => {
                      return {
                        ...pick(item, [`description`, `icon`, `isOwner`, `key`, `report`, `type`, `user`]),
                        label: truncate(item.label, 50),
                        value: item.label,
                        href: addOrUpdateWorkspaceIdFromUrl(item.link || item.href, getWorkspaceId()),
                      };
                    },
                  )
                : [
                    {
                      isReadOnly: true,
                      label: allowRecentItems ? `No recent history` : PROMPT_ALLOW_STORAGE_STRING,
                    },
                  ];
          }
          return [
            ...(!searchedText ? [this.getBrowseItem(searchedText)] : []),
            {
              label: `Recents`,
              isLoading: false,
              hideTopDivider: true,
              actionsRenderer: (item) => {
                if (items.length === 0 || items[0].isReadOnly) {
                  return null;
                }
                return (
                  <SvgIconEl
                    attrs={{icon: `x`}}
                    on={{
                      click: (ev) => {
                        // prevent href from going to link
                        ev.preventDefault();
                        this.helpers.handleClickRemove(ev, item);
                      },
                    }}
                    style={{
                      display: `flex`,
                      'margin-right': `8px`,
                      visibility: this.state.focusedItemKey === item.key ? `visible` : `hidden`,
                    }}
                  />
                );
              },
              iconRenderer: (item: SectionItem) => {
                if (items.length === 0 || !item.icon) {
                  return null;
                }
                return leftIconRenderer({
                  icon: item.icon,
                  tooltipText: SearchDisplayContextDisplayName[findKeyByValue(SearchDisplayContext, item.report)],
                });
              },
              items,
            },
          ];
        },
        handleSearchSelection: (ev) => {
          const {searchedText} = this.state;
          const item: RecentSearchItem = ev.detail.item;
          if (item.type === RecentSearchItemType.Entity) {
            this.track(SearchTrackedEvent.ResultSelected, this.getCommonTrackingPropertiesForItem(item));
            this.dispatchEvent(new CustomEvent(`entitySelected`, {detail: {href: item.href}}));
          } else {
            this.addRecentSearchItem(item.value);
            this.track(SearchTrackedEvent.RecentQuerySelected, {
              ...this.getCommonTrackingProperties(),
              [SearchTrackedProperty.SearchQuery]: searchedText,
            });
            if (this.helpers.enableBrowseAll()) {
              this.dispatchEvent(new CustomEvent(`browseAllSelected`));
            } else {
              this.dispatchEvent(new CustomEvent(`searchTermSelected`, {detail: {searchedText: item.value}}));
            }
          }
          // clear search field regardless of what you did
          this.update({searchedText: ``});
          // make sure whatever you select is updated
          this.updateRecentItemsAndFocusedItem();
        },
        handleFocusItem: (ev) => {
          this.update({focusedItemKey: ev.detail.item.key});
        },
        handleClickRemove: (ev, item) => {
          ev.stopPropagation();
          removeRecentItem(item);
          this.track(SearchTrackedEvent.RecentResultDeleted, this.getCommonTrackingPropertiesForItem(item));
          this.update({recentSearchItems: this.getRecentSearchEntities(getRecentItems())});
        },
        handleSearchFilterChange: (ev) => {
          const {searchFilter} = ev.detail;
          this.update({
            loading: true,
            searchResults: [],
            searchedText: searchFilter,
          });
          this.updateRecentItemsAndFocusedItem();
          // since empty string search results will never display in the dropdown
          if (searchFilter !== DEFAULT_SEARCH_QUERY_STRING) {
            this.executeSearch(searchFilter);
          }
        },
        handleSelectMenuChanged: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`menuChange`, {
              detail: {
                ...ev.detail,
                searchedText: this.state.searchedText,
              },
            }),
          );
          this.updateRecentItemsAndFocusedItem();
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.updateRecentItemsAndFocusedItem();
    this.updateAllowStorage();
    useStore(this, currentProjectStore);
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `open`) {
      this.updateRecentItemsAndFocusedItem();
    }
  }

  private async updateAllowStorage() {
    await window.gdprData?.waitUntilReady();
    this.update({allowRecentItems: window.gdprData?.allowStorage});
  }

  private updateRecentItemsAndFocusedItem() {
    const {searchedText} = this.state;
    // always just make this the first focused item
    const focusedItemKey = `${RecentSearchItemType.SearchTerm}-${searchedText}`;
    const recentSearchItems = this.getRecentSearchEntities(getRecentItems());
    this.update({
      focusedItemKey,
      recentSearchItems,
    });
  }

  private getBrowseItem(searchedText): Section {
    return {
      isLoading: false,
      iconRenderer: () => leftIconRenderer({icon: `arrow-diagonal`}),
      items: [
        {
          label:
            this.helpers.enableBrowseAll() || searchedText === DEFAULT_SEARCH_QUERY_STRING
              ? globalSearchBrowseReports
              : `Browse all results for "${searchedText}..."`,
          value: searchedText,
          key: `${RecentSearchItemType.SearchTerm}-${searchedText}`,
          type: RecentSearchItemType.SearchTerm,
        },
      ],
    };
  }

  private executeSearch = debounce(async (searchedText) => {
    const queryFilters: SearchQueryFilters = {
      email: [],
      type: getSearchContextsWithMaybeExplore(currentProjectStore.value).map((value) => ({value, negated: false})),
    };
    const trackingProps = {
      ...this.getCommonTrackingProperties(),
      [SearchTrackedProperty.OwnerFilters]: [],
      [SearchTrackedProperty.ReportFilters]: [],
      [SearchTrackedProperty.SearchQuery]: searchedText,
    };
    try {
      const searchResults = await executeSearch({queryFilters, querySort: null, searchedText, limit: 100});
      if (searchedText === this.state.searchedText && searchResults) {
        this.update({loading: false, searchResults});
        // by default items menu will auto-focus the first item. However, in this case the first item is the "No Results"
        // message, and we really want the second item, "Browse all" selected.
        window.requestAnimationFrame(() => {
          const selectEl = this.el.querySelector<SelectDropdown>(`mp-select`);
          if (selectEl && searchResults.length === 0) {
            this.helpers.enableBrowseAll()
              ? selectEl.focusItemWithLabel(globalSearchBrowseReports)
              : selectEl.focusItemWithLabel(`Browse all results for "${searchedText}..."`);
          }
        });
        this.updateRecentItemsAndFocusedItem();
        this.track(SearchTrackedEvent.SearchResultLoaded, {
          ...trackingProps,
          [SearchTrackedProperty.ResultsWithPopularity]: searchResults?.filter((item) => item.scores.popularity > 0)
            .length,
        });
      }
    } catch (err) {
      reporter.error(err);
      this.update({errored: true, loading: false});
      this.track(SearchTrackedEvent.SearchErrored, trackingProps);
    }
  }, 200);

  private addRecentSearchItem(text) {
    addRecentItem({
      icon: `search`,
      key: `${RecentSearchItemType.SearchTerm}-${text}`,
      label: text,
      type: RecentSearchItemType.SearchTerm,
    } as RecentSearchItem);
  }

  private getRecentSearchEntities(recentSearchItems = this.state.recentSearchItems) {
    // kept both item.link and item.href for backwards compatability
    return recentSearchItems?.filter((item) => item.type === RecentSearchItemType.Entity && (item.link || item.href));
  }

  private track(eventName: string, props: Obj = {}) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix: searchEventPrefix,
      props: {
        ...props,
        project_id: getProjectId(),
      },
      cancelable: true,
    });
  }

  private getCommonTrackingProperties() {
    return {
      ...getSearchCommonTrackingProperties(),
      [SearchTrackedProperty.Source]: SearchSource.Dropdown,
      [SearchTrackedProperty.ContentDiscoveryType]: this.state.searchedText
        ? ContentDiscoveryType.SearchQuery
        : ContentDiscoveryType.RecentResults,
    };
  }

  private getCommonTrackingPropertiesForItem(item) {
    return {
      ...this.getCommonTrackingProperties(),
      [SearchTrackedProperty.IsOwner]: item.isOwner,
      [SearchTrackedProperty.Owner]: item.user,
      [SearchTrackedProperty.ReportDescription]: item.description,
      [SearchTrackedProperty.ReportTitle]: item.label,
      [SearchTrackedProperty.ReportType]:
        SearchDisplayContextDisplayName[findKeyByValue(SearchDisplayContext, item.report)],
      [SearchTrackedProperty.ResultLink]: item.href,
    };
  }
}

export const GlobalSearchEl = defineAndMakeJsxFactory<GlobalSearchJsxProps>(`mp-global-search`, GlobalSearch);
